import { useGet, usePost } from "../../../packages/hooks";
import { FAQ } from "../../../types";
import { UseFaqsReturn } from "../types/faq-types";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { getAuthHeaders } from "../../../utils/auth-utils";
import { useAuthStore } from "../../../store/useAuth";

/**
 * Custom hook for FAQ management using hooks from @hooks/ package
 * @returns FAQ state and management functions
 */
export const useFaqs = (): UseFaqsReturn => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Initialize hooks
  const faqsQuery = useGet<{
    faqs: FAQ[];
    pagination: { total: number };
  }>(API_ENDPOINTS.FAQS.LIST, {
    onSuccess: (data) => {
      // Try multiple possible structures
      let faqList: FAQ[] = [];
      let totalCount = 0;

      if (data) {
        // Check for faqs array directly
        if (Array.isArray(data?.faqs)) {
          faqList = data.faqs;
          totalCount = data?.pagination?.total || faqList.length;
        }
        // Check for data.faqs structure
        else if (Array.isArray(data?.data?.faqs)) {
          faqList = data.data.faqs;
          totalCount = data?.data?.pagination?.total || faqList.length;
        }
        // Check if data itself is an array
        else if (Array.isArray(data)) {
          faqList = data;
          totalCount = data.length;
        }
        // Check for data.data array structure
        else if (Array.isArray(data?.data)) {
          faqList = data.data;
          totalCount = data.data.length;
        }
      }

      setFaqs(faqList);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      console.error("FAQs API error:", error);
      toast.error("Ошибка при загрузке FAQ");
    },
  });

  const createMutation = usePost<FAQ>(API_ENDPOINTS.FAQS.LIST, {
    onSuccess: () => {
      faqsQuery.refetch();
    },
    onError: () => {},
  });

  const fetchFaqs = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      await faqsQuery.refetch();
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Ошибка при загрузке FAQ");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Don't include faqsQuery - it changes on every render

  const handleCreateFaq = async (values: Partial<FAQ>): Promise<any> => {
    try {
      setIsLoading(true);
      const response = await createMutation.mutateAsync({
        question: values.question || "",
        answer: values.answer || "",
      });
      return response;
    } catch (error) {
      console.error("Error creating FAQ:", error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateFaq = async (
    id: string,
    values: Partial<FAQ>
  ): Promise<any> => {
    try {
      setIsLoading(true);

      // Use the correct endpoint with FAQ ID for update
      const url = buildApiUrl(API_ENDPOINTS.FAQS.BY_ID(id));
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(API_ENDPOINTS.FAQS.BY_ID(id), token),
        },
        body: JSON.stringify({
          id: id,
          question: values.question || "",
          answer: values.answer || "",
        }),
      });

      if (!response.ok) {
        // Try to parse error response from backend
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            const error = new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
            (error as any).data = errorData;
            (error as any).status = response.status;
            (error as any).statusText = response.statusText;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, fall back to generic error
          if (
            parseError instanceof Error &&
            parseError.message !== `HTTP error! status: ${response.status}`
          ) {
            throw parseError; // Re-throw if it's our custom error
          }
        }

        // Create a more descriptive error for different status codes
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (response.status === 400) {
          errorMessage =
            "Неверный запрос. Проверьте данные и попробуйте снова.";
        } else if (response.status === 401) {
          errorMessage =
            "Необходима авторизация для выполнения этого действия.";
        } else if (response.status === 403) {
          errorMessage = "У вас нет прав для выполнения этого действия.";
        } else if (response.status === 404) {
          errorMessage = "Запрашиваемый ресурс не найден.";
        } else if (response.status === 409) {
          errorMessage =
            "Конфликт данных. Возможно, ресурс используется в других местах.";
        } else if (response.status === 422) {
          errorMessage = "Ошибка валидации данных.";
        } else if (response.status >= 500) {
          errorMessage = "Внутренняя ошибка сервера. Попробуйте позже.";
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        throw error;
      }

      // Handle CRM API response format

      faqsQuery.refetch();
      return response;
    } catch (error) {
      console.error("Error updating FAQ:", error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFaq = async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Use the correct endpoint with FAQ ID for delete
      const url = buildApiUrl(API_ENDPOINTS.FAQS.BY_ID(id));
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: getAuthHeaders(API_ENDPOINTS.FAQS.BY_ID(id), token),
      });

      if (!response.ok) {
        // Try to parse error response from backend
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            const error = new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
            (error as any).data = errorData;
            (error as any).status = response.status;
            (error as any).statusText = response.statusText;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, fall back to generic error
          if (
            parseError instanceof Error &&
            parseError.message !== `HTTP error! status: ${response.status}`
          ) {
            throw parseError; // Re-throw if it's our custom error
          }
        }

        // Create a more descriptive error for different status codes
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (response.status === 400) {
          errorMessage =
            "Неверный запрос. Проверьте данные и попробуйте снова.";
        } else if (response.status === 401) {
          errorMessage =
            "Необходима авторизация для выполнения этого действия.";
        } else if (response.status === 403) {
          errorMessage = "У вас нет прав для выполнения этого действия.";
        } else if (response.status === 404) {
          errorMessage = "Запрашиваемый ресурс не найден.";
        } else if (response.status === 409) {
          errorMessage =
            "Конфликт данных. Возможно, ресурс используется в других местах.";
        } else if (response.status === 422) {
          errorMessage = "Ошибка валидации данных.";
        } else if (response.status >= 500) {
          errorMessage = "Внутренняя ошибка сервера. Попробуйте позже.";
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        throw error;
      }

      // Handle CRM API response format
      toast.success("FAQ успешно удален");
      faqsQuery.refetch();
      return true;
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      const message = (error as any)?.message || "Ошибка при удалении FAQ";
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch only - avoid infinite loop
    const initialFetch = async () => {
      try {
        setIsLoading(true);
        await faqsQuery.refetch();
      } catch (error) {
        console.error("Error fetching FAQs:", error);
        toast.error("Ошибка при загрузке FAQ");
      } finally {
        setIsLoading(false);
      }
    };

    initialFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    faqs,
    isLoading:
      isLoading ||
      faqsQuery.isLoading ||
      faqsQuery.isFetching ||
      createMutation.isPending,
    totalItems,
    fetchFaqs,
    handleCreateFaq,
    handleUpdateFaq,
    handleDeleteFaq,
  };
};
